<template>
  <div class="library-content-header">
    <div class="library-content-header__top-row">
      <div class="library-content-header__title-group">
        <h1
          class="library-content-header__title"
          data-testid="headerTitle"
        >
          {{ libraryTitle }}
        </h1>

        <span
          v-if="itemsCount"
          class="library-content-header__count"
        >
          {{ itemsCount }}
          {{ itemsCount === 1 ? 'result' : 'results' }}
        </span>

        <span
          v-else
          class="library-content-header__count-skeleton"
        />
      </div>

      <div class="library-content-header__wrapper-options">
        <div class="library-content-header__inputs">
          <!-- SEARCH -->
          <ElementInput
            ref="searchInput"
            v-model="searchValue"
            placeholder="Search"
            size="small"
            clearable
            class="library-content-header__search"
          >
            <i
              slot="prefix"
              class="el-input__icon el-icon-search"
            />
          </ElementInput>

          <!-- SORTING -->
          <ElementCascader
            v-if="sorting"
            ref="sortCascader"
            v-model="currentSorting"
            placeholder="Sort"
            class="library-content-header__select library-content-header__select--small"
            :options="librarySortOptions"
            :props="{ expandTrigger: 'hover', emitPath: false }"
            @change="sortItems"
          />

          <!-- UPLOAD Button -->
          <StitchTooltip
            :message="newItemDisabledMessage"
            :is-disabled="!disableCreateButton"
          >
            <ElementButton
              v-if="showCreateButton"
              :disabled="disableCreateButton"
              type="primary"
              plain
              class="library-content-header__button-create"
              @click="toggleLibraryItemCreateForm"
            >
              {{ newItemTitle }}
            </ElementButton>
          </StitchTooltip>

          <!-- Bulk Create Button -->
          <StitchTooltip
            :message="newItemDisabledMessage"
            :is-disabled="!disableCreateButton"
          >
            <ElementButton
              v-if="
                canTeamAccessStyleBulkCreation &&
                  showCreateButton &&
                  libraryType === LIBRARY_TYPE.STYLE &&
                  !isVendorUser
              "
              :disabled="disableCreateButton"
              type="primary"
              plain
              class="library-content-header__button-create"
              @click="navigateToBulkCreate"
            >
              Style Bulk Creation
            </ElementButton>
          </StitchTooltip>

          <!-- Style Bulk Actions Button -->
          <StitchTooltip :message="'Bulk edit multiple styles'">
            <ElementButton
              v-if="
                canTeamAccessStyleBulkActions &&
                  showCreateButton &&
                  libraryType === LIBRARY_TYPE.STYLE &&
                  !isVendorUser
              "
              :disabled="isLoading"
              type="primary"
              plain
              class="library-content-header__button-create"
              @click="$emit('toggle-style-bulk-actions-toolbar', true)"
            >
              Style Bulk Actions
            </ElementButton>
          </StitchTooltip>

          <StitchTooltip :message="'Export Styles as Excel'">
            <!-- Styles Export Button -->
            <ElementButton
              v-if="
                libraryType === LIBRARY_TYPE.STYLE &&
                  !isWatchlistPage &&
                  !isVendorUser
              "
              type="primary"
              plain
              size="small"
              :loading="isExportingStyles"
              @click="handleStylesExport"
            >
              <img
                src="@/assets/icons/excel.svg"
                alt="Export to Excel"
                class="library-content-header__button-excel-icon"
              >
            </ElementButton>
          </StitchTooltip>

          <!-- FILTERS -->
          <ElementButton
            type="primary"
            plain
            :class="[
              'library-content-header__button-filter',
              {
                'library-content-header__button-filter--active': filtersOpen
              }
            ]"
            :aria-expanded="String(filtersOpen)"
            :aria-pressed="String(filtersOpen)"
            aria-controls="library-filters-sidebar"
            :title="filtersOpen ? 'Close filters' : 'Open filters'"
            @click="$emit('toggle-filters')"
          >
            <span class="library-content-header__button-filter-content">
              <i
                class="el-icon-s-operation library-content-header__button-filter-icon"
                aria-hidden="true"
              />
              <span>Filters</span>
            </span>
          </ElementButton>
        </div>
      </div>
    </div>

    <!-- Bulk Actions Toolbar -->
    <div
      v-if="showStyleBulkActionsToolbar"
      class="library-content-header__bulk-toolbar"
    >
      <div class="library-content-header__bulk-section">
        <ElementCheckbox
          :value="areAllVisibleSelected"
          @change="$emit('toggle-select-visible-styles', $event)"
        />

        <span class="library-content-header__bulk-count">
          {{ selectedStyleIds.length }} selected
        </span>

        <ElementButton
          type="text"
          class="library-content-header__bulk-clear"
          @click="$emit('clear-style-selection')"
        >
          Clear selection
        </ElementButton>
      </div>

      <div class="library-content-header__bulk-section">
        <ElementDropdown
          trigger="click"
          class="library-content-header__bulk-status-dropdown"
          @command="handleBulkStatusUpdate"
        >
          <ElementButton
            size="small"
            :disabled="selectedStyleIds && selectedStyleIds.length === 0"
            class="library-content-header__bulk-status-trigger"
          >
            Change status
            <i class="el-icon-arrow-down el-icon--right" />
          </ElementButton>
          <ElementDropdownMenu
            slot="dropdown"
            class="library-content-header__bulk-status-menu"
          >
            <ElementDropdownItem
              v-for="status in bulkUpdateStatusTransitions"
              :key="status.value"
              :command="status.value"
            >
              {{ status.label }}
            </ElementDropdownItem>
          </ElementDropdownMenu>
        </ElementDropdown>

        <ElementButton
          type="text"
          class="library-content-header__bulk-delete"
          @click="$emit('bulk-delete', selectedStyleIds)"
        >
          Delete
        </ElementButton>
      </div>

      <ElementButton
        type="text"
        class="library-content-header__bulk-cancel"
        @click="$emit('toggle-style-bulk-actions-toolbar', false)"
      >
        <i class="el-icon-close" />
        Cancel
      </ElementButton>
    </div>

    <FormGenerator
      :show-form-generator="showLibraryItemCreateForm"
      :form-configuration="formConfiguration"
      :options="camelCasedFilters"
      :default-data="preFilledData"
      :additional-data="additionalData"
      :is-creation="true"
      @form-close="onCreateFormClose"
      @form-complete="formComplete"
    />
  </div>
</template>

<script>
import _debounce from 'lodash/debounce'
import { mapGetters, mapActions } from 'vuex'
import VueTypes from 'vue-types'

import Utils from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'

import { FiltersPersistency } from '@/mixins/FiltersPersistency.js'

import { LIBRARY_TYPE } from '@/constants/libraryType'
import { FILTER_TYPE } from '@/constants/filterType'
import { SORT_OPTION } from '@/constants/sortOption'
import FeatureFlags from '@/services/featureFlags'
import { USER_ROLE } from '@/constants/roleType'
import { ASSET_STATUS, ASSET_STATUSES_ORDER } from '@/constants/assetStatuses'

export default {
  name: 'LibraryContentHeader',

  mixins: [FiltersPersistency],

  props: {
    sorting: VueTypes.oneOf(Object.values(SORT_OPTION)),
    newItemFiles: VueTypes.instanceOf(FileList),
    showStyleBulkActionsToolbar: VueTypes.bool.def(false),
    selectedStyleIds: VueTypes.array.def(() => []),
    itemsCount: VueTypes.number,
    areAllVisibleSelected: VueTypes.bool.def(false),
    isLoading: VueTypes.bool.def(false),
    filtersOpen: VueTypes.bool.def(false)
  },

  data () {
    return {
      LIBRARY_TYPE,
      showLibraryItemCreateForm: false,
      searchValue: '',
      currentSorting: null,
      defaultDataForm: {},
      isExportingStyles: false,
      selectedStylesCount: 12,
      libraryStrings: {
        [LIBRARY_TYPE.BLOCK]: {
          title: 'Blocks',
          newItemButton: 'New Block',
          disabledMessage: 'As a vendor user, you cannot create Blocks.'
        },
        [LIBRARY_TYPE.FABRIC]: {
          title: 'Fabrics',
          newItemButton: 'New Fabric',
          disabledMessage: 'As a vendor user, you cannot create Fabrics.'
        },
        [LIBRARY_TYPE.STITCH]: {
          title: 'Stitches',
          newItemButton: 'New Stitch',
          disabledMessage: 'As a vendor user, you cannot create Stitches.'
        },
        [LIBRARY_TYPE.COLOR]: {
          title: 'Color Cards',
          newItemButton: 'New Color Card',
          disabledMessage: 'As a vendor user, you cannot create Color Cards.'
        },
        [LIBRARY_TYPE.TRIM]: {
          title: 'Trims',
          newItemButton: 'New Trim',
          disabledMessage: 'As a vendor user, you cannot create Trims.'
        },
        [LIBRARY_TYPE.STYLE]: {
          title: 'Styles',
          newItemButton: 'New Style',
          disabledMessage: 'Select a team before creating a new Style.'
        },
        [LIBRARY_TYPE.JOB]: {
          title: 'My Renders'
        }
      },
      sortOptions: {
        library: [
          {
            label: 'Last Modified',
            value: SORT_OPTION.LAST_MODIFIED
          },
          {
            label: 'First Modified',
            value: SORT_OPTION.FIRST_MODIFIED
          },
          {
            label: 'Sort By Name',
            value: SORT_OPTION.NAME
          }
        ],
        styles: [
          {
            label: 'Newest First',
            value: SORT_OPTION.NEWEST_FIRST
          },
          {
            label: 'Oldest First',
            value: SORT_OPTION.OLDEST_FIRST
          },
          {
            label: 'Last Modified First',
            value: SORT_OPTION.LAST_MODIFIED
          },
          {
            label: 'Sort By Name',
            value: SORT_OPTION.NAME
          }
        ],
        jobs: [
          {
            label: 'Newest First',
            value: SORT_OPTION.NEWEST_FIRST
          },
          {
            label: 'Oldest First',
            value: SORT_OPTION.OLDEST_FIRST
          },
          {
            label: 'Sort By Name',
            value: SORT_OPTION.NAME
          }
        ]
      },
      formConfiguration: null
    }
  },

  computed: {
    ...mapGetters([
      'getSearch',
      'getAvailableFilters',
      'getAppliedFilters',
      'getActiveStyle'
    ]),

    ...mapGetters({
      user: 'getCognitoUserData',
      libraryType: 'getActiveLibraryType',
      jobSearch: 'getJobSearch',
      browzwearAPI: 'getBrowzwearAPI'
    }),

    /**
     * @returns {Array}
     */
    bulkUpdateStatusTransitions () {
      const transitions = Object.values(ASSET_STATUS).filter(
        item => item.value !== ASSET_STATUS.RESUBMIT.value
      )

      return transitions.sort((a, b) => {
        return (
          ASSET_STATUSES_ORDER.indexOf(a.value) -
          ASSET_STATUSES_ORDER.indexOf(b.value)
        )
      })
    },

    /**
     * @returns {Array}
     */
    availableFilters () {
      return this.getAvailableFilters()
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    canAccessRenderEngineChoice () {
      return FeatureFlags.canTeamAccessRenderEngineChoice()
    },

    /**
     * @returns {boolean}
     */
    canTeamAccessStyleBulkCreation () {
      return (
        FeatureFlags.canTeamAccessStyleBulkCreation() &&
        FeatureFlags.canTeamAccessStyleCreation()
      )
    },

    /**
     * @returns {boolean}
     */
    canTeamAccessStyleBulkActions () {
      return FeatureFlags.canTeamAccessStyleBulkActions()
    },

    /**
     * @returns {boolean}
     */
    canTeamAccessStyleCreation () {
      return FeatureFlags.canTeamAccessStyleCreation()
    },

    /**
     * for matching the filter/keys in the item
     *
     * @returns {object}
     */
    camelCasedFilters () {
      if (this.canAccessRenderEngineChoice) {
        return Utils.convertObjectKeysToCamelCase({
          ...FILTER_TYPE.RENDER_ENGINE_FILTERS,
          ...this.availableFilters
        })
      }

      return Utils.convertObjectKeysToCamelCase({
        ...this.availableFilters
      })
    },

    /**
     * @returns {object}
     */
    search () {
      return this.getSearch()
    },

    /**
     * @returns {Array}
     */
    appliedFilters () {
      return this.getAppliedFilters()
    },

    /**
     * @returns {object}
     */
    activeStyle () {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return null
      }

      return this.getActiveStyle
    },

    /**
     * @returns {object}
     */
    preFilledData () {
      const appliedFiltersFirstElements = {}
      Object.entries(this.appliedFilters || {}).forEach(([key, value]) => {
        let newValue = value

        if (Array.isArray(value)) {
          newValue = value.length > 0 ? value[0] : null
        }

        appliedFiltersFirstElements[key] = newValue
      })

      const singleGroupAccess = this.getDefaultUserGroupFilter()

      // for vendors, company is always added by default
      // the role is used to distinguish between users
      // inside the FormGenerator
      if (this.isVendorUser) {
        appliedFiltersFirstElements.vendorCompanyId =
          this.user.vendor_company.id
        appliedFiltersFirstElements.role = USER_ROLE.VENDOR.name
      }

      return Utils.convertObjectKeysToCamelCase({
        ...appliedFiltersFirstElements,
        ...singleGroupAccess,
        ...this.defaultDataForm,
        ...(this.libraryType === LIBRARY_TYPE.STYLE && this.activeStyle
          ? {
              ...this.activeStyle,
              ...this.activeStyle.attributes
            }
          : {})
      })
    },

    /**
     * @returns {Array}
     */
    userGroupIds () {
      return this.user.teams.map(team => team.id)
    },

    /**
     * @returns {Array}
     */
    currentGroupFilters () {
      const appliedGroupFilter = this.appliedFilters.division_id || []
      const persistentGroupFilter =
        this.getPersistentFilters(this.libraryType).division_id || []

      return Array.from(
        new Set([...appliedGroupFilter, ...persistentGroupFilter])
      )
    },

    /**
     * @returns {object}
     */
    additionalData () {
      // add fields to multiple-division items
      if ([LIBRARY_TYPE.TRIM, LIBRARY_TYPE.FABRIC].includes(this.libraryType)) {
        // If there's an applied division filter, use it as default
        const mergedGroupFiltersAndUserPermissions = Array.from(
          new Set([...this.currentGroupFilters, ...this.userGroupIds])
        )

        return { divisionIds: [mergedGroupFiltersAndUserPermissions[0]] }
      }

      if (this.isStyleLibrary || this.isBlockLibrary) {
        return { isMain: true }
      }

      return null
    },

    /**
     * @returns {Array}
     */
    librarySortOptions () {
      if (this.isJobLibrary) {
        return this.sortOptions.jobs
      } else if (this.isStyleLibrary) {
        return this.sortOptions.styles
      } else {
        return this.sortOptions.library
      }
    },

    /**
     * @returns {string}
     */
    libraryTitle () {
      if (this.isWatchlistPage) {
        return 'My watchlist'
      }

      return this.libraryStrings[this.libraryType].title || 'DesignHUB'
    },

    /**
     * @returns {string}
     */
    newItemTitle () {
      return this.libraryStrings[this.libraryType].newItemButton || 'New Item'
    },

    /**
     * @returns {string}
     */
    newItemDisabledMessage () {
      if (this.isStyleLibrary && !this.canTeamAccessStyleCreation) {
        return 'Style creation is disabled for this team.'
      }

      return this.libraryStrings[this.libraryType].disabledMessage || ''
    },

    /**
     * @returns {boolean}
     */
    isStyleLibrary () {
      return this.libraryType === LIBRARY_TYPE.STYLE
    },

    /**
     * @returns {boolean}
     */
    isBlockLibrary () {
      return this.libraryType === LIBRARY_TYPE.BLOCK
    },

    /**
     * @returns {boolean}
     */
    isJobLibrary () {
      return this.libraryType === LIBRARY_TYPE.JOB
    },

    /**
     * @returns {boolean}
     */
    isWatchlistPage () {
      return this.$route.name === 'LibraryStylesWatchlist'
    },

    /**
     * @returns {boolean}
     */
    showCreateButton () {
      return !(this.isJobLibrary || this.browzwearAPI || this.isWatchlistPage)
    },

    /**
     * @returns {boolean}
     */
    disableCreateButton () {
      return (
        this.$route?.query?.group_id === 'all' ||
        this.$route?.query?.division_id === 'all' ||
        (this.isVendorUser && this.libraryType !== LIBRARY_TYPE.STYLE) ||
        (this.isStyleLibrary && !this.canTeamAccessStyleCreation)
      )
    }
  },

  watch: {
    /**
     * @param {string} to
     * @param {string} from
     */
    $route (to, from) {
      this.getFormConfiguration()

      if (from.name !== to.name) {
        if (this.isJobLibrary) {
          this.searchValue = this.jobSearch
        } else {
          this.searchValue = this.search
        }

        this.$refs.searchInput.focus()
      }
    },

    /**
     */
    searchValue () {
      this.searchLibrary()
    },

    /**
     */
    sorting () {
      if (this.sorting) {
        this.updateSortItems()
      }
    },

    /**
     */
    newItemFiles () {
      if (this.newItemFiles && this.newItemFiles.length > 0) {
        const newItemFile = this.newItemFiles[0]
        const name = Utils.getFileNameWithoutExtension(newItemFile.name)

        this.defaultDataForm = {
          model3D: [
            {
              name,
              description: name,
              assets_url: newItemFile
            }
          ]
        }

        this.toggleLibraryItemCreateForm()
      }
    }
  },

  async mounted () {
    if (this.libraryType !== LIBRARY_TYPE.JOB) {
      await this.fetchAvailableFilters()
    }

    this.getFormConfiguration()

    if (this.sorting) {
      this.updateSortItems()
    }

    this.initializeFilters()

    this.$refs.searchInput.focus()
  },

  methods: {
    ...mapActions([
      'setSearch',
      'resetItems',
      'setJobSearch',
      'resetJobs',
      'fetchAvailableFilters',
      'exportStyles'
    ]),

    /**
     *
     */
    initializeFilters () {
      const persistentFilters = this.getPersistentFilters(this.libraryType)
      const stateSearch = this.isJobLibrary ? this.jobSearch : this.search

      this.searchValue = persistentFilters.search || stateSearch || ''

      if (this.searchValue !== '') {
        this.searchLibrary()
      }
    },

    /**
     *
     */
    getFormConfiguration () {
      const config = getFormConfig(this.libraryType)

      if (this.libraryType === LIBRARY_TYPE.STYLE) {
        config.steps[2].fields[1].visible = this.canAccessRenderEngineChoice
      } else if (this.libraryType === LIBRARY_TYPE.BLOCK) {
        config.steps[1].fields[1].visible = this.canAccessRenderEngineChoice
      }

      this.formConfiguration = config
    },

    /**
     */
    sortItems () {
      this.$emit('sorting-change', this.currentSorting)
    },

    /**
     */
    toggleLibraryItemCreateForm () {
      this.showLibraryItemCreateForm = !this.showLibraryItemCreateForm
    },

    /**
     *
     * @param {string} status
     */
    handleBulkStatusUpdate (status) {
      this.$emit('bulk-update-status', {
        status: status,
        styleIds: this.selectedStyleIds
      })
    },

    /**
     * @param {boolean} success
     */
    formComplete (success) {
      if (success) {
        this.toggleLibraryItemCreateForm()
      }
    },

    /**
     */
    onCreateFormClose () {
      this.showLibraryItemCreateForm = false
      this.defaultDataForm = {}

      // Clears the active style set during creation so its data does not
      // pre-fill the next new-style form via preFilledData.
      if (this.isStyleLibrary) {
        this.$store.commit('clearActiveStyle')
      }
    },

    /**
     *
     */
    navigateToBulkCreate () {
      this.$router.push({
        name: 'BulkActions',
        query: { group_id: this.$route.query.group_id }
      })
    },

    /**
     *
     */
    async handleStylesExport () {
      this.isExportingStyles = true
      try {
        await this.exportStyles()
      } catch (error) {
        let errorMessage = 'Invalid request!'
        const data = error.response.data

        if (data instanceof Blob) {
          const text = await data.text()
          const payload = JSON.parse(text)
          errorMessage = payload.detail || text
        }

        this.$message({
          message: `Export failed! ${errorMessage}`,
          type: 'error',
          showClose: true,
          duration: 4000
        })
      } finally {
        this.isExportingStyles = false
      }
    },

    /**
     *
     */
    searchLibrary: _debounce(
      /**
       * This function is not an arrow function because this (Vue) is being used.
       */
      function () {
        let search = this.search

        if (this.isJobLibrary) {
          search = this.jobSearch
        }

        this.buildUrl({
          filterGroupName: 'search',
          value: this.searchValue
        })

        if (search === this.searchValue) {
          return
        }

        if (this.isJobLibrary) {
          this.setJobSearch(this.searchValue)
          this.resetJobs()
        } else {
          this.setSearch({ searchQuery: this.searchValue })
          this.resetItems()
        }

        this.$emit('search-update')
      },
      250
    ),

    /**
     */
    updateSortItems () {
      this.currentSorting = this.sorting
    },

    /**
     * @returns {object}
     */
    getDefaultUserGroupFilter () {
      if (this.user.teams?.length === 1) {
        return { [FILTER_TYPE.GROUP_ID]: this.user.teams[0].id }
      }

      return {}
    }
  }
}
</script>

<style lang="scss" scoped>
$content-header-search-width: 210px;
$content-header-input-min-width: spacing(12);
$button-small-padding: 10px 15px;
$title-top: 12px;
$icon-size: 16px;
$selection-count-font-weight: 600;
$filter-button-icon-size: spacing(2);
$filter-button-active-shadow: 0 spacing(0.5) spacing(1.5) rgba($blue, 0.22);

.library-content-header {
  position: relative;
  width: 100%;
  margin-bottom: spacing(2);
}

.library-content-header__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: spacing(2);
  padding-left: spacing(2);

  @media (max-width: $media-query-l) {
    flex-wrap: wrap;
  }
}

.library-content-header__title-group {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-top: spacing(1.5);
}

.library-content-header__title {
  margin: 0;
  padding-right: spacing(2);
  color: $grey-dark;
  line-height: 1.1;
  text-transform: uppercase;
}

.library-content-header__count {
  @include text-body;

  margin-top: spacing(0.25);
  margin-left: spacing(0.25);
  color: $grey;
  font-size: $font-size-label;
  line-height: 1.2;
}

.library-content-header__count-skeleton {
  width: spacing(10);
  height: spacing(1.5);
  margin-top: spacing(0.5);
  margin-left: spacing(0.25);
  background: linear-gradient(90deg, #f7f8fa 25%, #f1f3f5 37%, #f7f8fa 63%);
  background-size: 400% 100%;
  border-radius: spacing(0.5);
  animation: skeleton-loading 1.6s ease infinite;
}

.library-content-header__wrapper-options {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}

.library-content-header__inputs {
  display: flex;
  flex: 1 1 auto;
  gap: spacing(1);
  align-items: center;
  justify-content: flex-end;
  width: percentage(100);
  min-width: 0;

  > * {
    margin-right: 0;
  }
}

.library-content-header__inputs > *:last-child .el-button {
  padding: spacing(1) spacing(2);
}

.library-content-header__select {
  max-width: spacing(21);
  margin-right: 0;

  @media (min-width: $media-query-m) {
    min-width: $content-header-input-min-width;
  }

  @media (max-width: $media-query-s) {
    display: none;
  }
}

.library-content-header__select--small {
  /deep/ .el-input {
    @media (min-width: $media-query-l) {
      min-width: $content-header-input-min-width;
    }
  }
}

.library-content-header__search {
  flex: 0 1 $content-header-search-width;
  width: $content-header-search-width;
  max-width: $content-header-search-width;
  margin-right: auto;

  @media (min-width: $media-query-m) {
    min-width: $content-header-input-min-width;
  }
}

.library-content-header__button-filter,
.library-content-header__button-create {
  padding: $button-small-padding;
  text-transform: capitalize;
}

.library-content-header__button-filter {
  transition:
    color $transition,
    background-color $transition,
    border-color $transition,
    box-shadow $transition;
}

.library-content-header__button-filter-content {
  display: inline-flex;
  gap: spacing(0.75);
  align-items: center;
  justify-content: center;
}

.library-content-header__button-filter-icon {
  width: $filter-button-icon-size;
  font-size: $filter-button-icon-size;
  line-height: 1;
  text-align: center;
}

.library-content-header__button-filter:hover,
.library-content-header__button-filter:focus {
  color: $blue;
  background-color: rgba($blue, 0.08);
  border-color: rgba($blue, 0.45);
}

.library-content-header__button-filter--active,
.library-content-header__button-filter--active:hover,
.library-content-header__button-filter--active:focus {
  color: $white;
  background-color: $blue;
  border-color: $blue;
  box-shadow: $filter-button-active-shadow;
}

.library-content-header__button-excel-icon {
  width: $icon-size;
  height: $icon-size;
  vertical-align: middle;
}

/* ===========================
 * Bulk Actions Toolbar
 * =========================== */

.library-content-header__bulk-toolbar {
  display: flex;
  align-items: center;
  width: percentage(100);
  height: spacing(6);
  padding: 0 spacing(2.5);
  background:
    linear-gradient(
      180deg,
      rgba(64, 158, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.02) 100%
    ),
    linear-gradient(
      90deg,
      rgba(64, 158, 255, 0.04) 0%,
      rgba(64, 158, 255, 0.02) 50%,
      rgba(64, 158, 255, 0.04) 100%
    ),
    $white;
  border-top: 1px solid $blue-light;
}

.library-content-header__bulk-section {
  display: flex;
  align-items: center;

  &:nth-child(1) {
    gap: spacing(3);
  }

  &:nth-child(2) {
    gap: spacing(3);
    margin-right: spacing(5);
    margin-left: auto;
  }
}

.library-content-header__bulk-count {
  color: $grey-dark;
  font-weight: $selection-count-font-weight;
  font-size: $font-size-label-medium;
  white-space: nowrap;
}

.library-content-header__bulk-toolbar /deep/ .el-checkbox {
  margin: 0;
}

.library-content-header__bulk-toolbar /deep/ .el-checkbox__inner {
  width: spacing(2);
  height: spacing(2);
}

.library-content-header__bulk-toolbar /deep/ .el-button--text {
  padding: 0;
  font-weight: $selection-count-font-weight;
  font-size: $font-size-label-medium;
}

.library-content-header__bulk-clear {
  color: $blue !important;
}

.library-content-header__bulk-delete {
  color: $red !important;
}

.library-content-header__bulk-cancel {
  display: flex;
  gap: spacing(0.8);
  align-items: center;
  color: $grey !important;
}

.library-content-header__bulk-cancel i {
  font-size: spacing(2);
}

.library-content-header__bulk-status-trigger {
  min-width: spacing(14);
  font-weight: $selection-count-font-weight;
}
</style>

<style lang="scss">
.library-content-header__bulk-status-option {
  display: flex;
  align-items: center;
  min-width: spacing(15);
  padding: spacing(0.75) spacing(1.25);
  color: $white;
  font-weight: $font-weight-bold;
  font-size: spacing(1.25);
  border-radius: spacing(2.5);
}
</style>
